import { Modal, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getIso7010SignImagePath,
  iso7010SignCategories,
  iso7010SignDescriptions,
  Iso7010SignCode,
} from '../../constants';
import {
  CategorySection,
  CategoryTitle,
  ModalBody,
  SignGrid,
  SignItem,
  SignTitle,
} from './styles';

export interface SelectSignsModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (signs: Iso7010SignCode[]) => void;
  selectedSigns?: Iso7010SignCode[];
  readOnly?: boolean;
}

const SelectSignsModal = ({
  open,
  onClose,
  onConfirm,
  selectedSigns = [],
  readOnly = false,
}: SelectSignsModalProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Iso7010SignCode[]>([]);

  useEffect(() => {
    if (open) {
      setDraft([...selectedSigns]);
    }
  }, [open, selectedSigns]);

  const toggleSign = (code: Iso7010SignCode) => {
    setDraft((prev) =>
      prev.includes(code) ? prev.filter((s) => s !== code) : [...prev, code]
    );
  };

  const handleConfirm = () => {
    onConfirm(draft);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="Select Safety Instructions "
      onCancel={onClose}
      onOk={handleConfirm}
      okText={`${t('confirm')}${draft.length ? ` (${draft.length})` : ''}`}
      cancelText={t('cancel')}
      width={720}
      destroyOnClose
    >
      <ModalBody>
        {Object.values(iso7010SignCategories).map((category) => (
          <CategorySection key={category.title}>
            <CategoryTitle>{category.title}</CategoryTitle>
            <SignGrid>
              {category.icons.map((code) => {
                const selected = draft.includes(code);
                const description = iso7010SignDescriptions[code];
                const signContent = (
                  <SignItem
                    key={code}
                    type="button"
                    $selected={selected}
                    onClick={() => !readOnly && toggleSign(code)}
                    aria-pressed={selected}
                    aria-label={code}
                  >
                    <img
                      src={getIso7010SignImagePath(code)}
                      alt={code}
                      loading="lazy"
                    />
                    <SignTitle>
                      <strong>{code}</strong> - {description}
                    </SignTitle>
                  </SignItem>
                );

                return readOnly ? (
                  <Tooltip key={code} title={description}>
                    {signContent}
                  </Tooltip>
                ) : (
                  signContent
                );
              })}
            </SignGrid>
          </CategorySection>
        ))}
      </ModalBody>
    </Modal>
  );
};

export default SelectSignsModal;
